import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link, useParams, useHistory } from 'react-router-dom';
import Select from 'react-select';
import BlockUi from 'react-block-ui';
import Swal from 'sweetalert2';
import Api from '../../api/global.service';
import { Panel, PanelBody } from '../../components/panel/panel.jsx';

// ─── Boolean checkbox fields ──────────────────────────────────────────────────

const MODULES_FIELDS = [
  { name: 'usa_sic', label: 'Sistema de Control Interno (SIC)' },
  { name: 'usa_comite_evaluacion', label: 'Comité de Evaluación' },
  { name: 'usa_acopio', label: 'Acopio' },
  { name: 'usa_calidad', label: 'Control de Calidad' },
  { name: 'usa_whatsapp', label: 'Envíos de fichas por WhatsApp' },
  { name: 'usa_eudr', label: 'EUDR' },
  { name: 'usa_ra', label: 'Regenerativo (RA)' },
  { name: 'usa_cj', label: 'Comercio Justo (CJ)' },
  { name: 'usa_centros_acopios', label: 'Centros de acopio' },
  { name: 'usa_estimacion_manual', label: 'Estimación manual de cosecha' },
  { name: 'usa_codigo_tx', label: 'Código de transacción' },
  { name: 'esta_en_map', label: 'Aparece en mapa' },
];

const ACOPIO_FIELDS = [
  { name: 'nueva_cuenta_acopio', label: 'Nueva cuenta de acopio' },
  { name: 'estimacion_cosecha', label: 'Estimación de cosecha' },
  { name: 'adelanto_efectivo', label: 'Adelanto en efectivo' },
  { name: 'precio_por_kg_editable', label: 'Precio por kg editable' },
  { name: 'precio_por_kg_precargado', label: 'Precio por kg precargado' },
  { name: 'precio_por_kg_por_productor', label: 'Precio por kg por productor' },
  { name: 'detalle_ids_productores', label: 'Detalle de IDs de productores' },
  { name: 'detalle_parcelas', label: 'Detalle de parcelas' },
  { name: 'acopio_entrega_separada', label: 'Entrega separada' },
  { name: 'acopio_pagos_por_kilos', label: 'Pagos por kilos' },
  { name: 'remanente_tipo', label: 'Remanente tipo' },
];

const VISUALIZACION_FIELDS = [
  { name: 'mostrar_certificadora', label: 'Mostrar certificadora' },
  { name: 'mostrar_muestreo', label: 'Mostrar muestreo' },
  { name: 'mostrar_tipos', label: 'Mostrar tipos de café' },
  { name: 'mostrar_lotes', label: 'Mostrar lotes' },
  { name: 'mostrar_costaleras', label: 'Mostrar costaleras' },
  { name: 'mostrar_descuento', label: 'Mostrar descuento' },
  { name: 'mostrar_descuento_humedad', label: 'Mostrar descuento por humedad' },
  { name: 'mostrar_descuentos_porcentaje', label: 'Mostrar descuentos en porcentaje' },
  { name: 'mostrar_efectivo_dashboard_app', label: 'Mostrar efectivo en dashboard app' },
  { name: 'mostrar_dashboard_inicio', label: 'Mostrar dashboard inicio' },
  { name: 'mostrar_dashboard_productores', label: 'Mostrar dashboard productores' },
  { name: 'mostrar_dashboard_rechazados', label: 'Mostrar dashboard rechazados' },
  { name: 'mostrar_padron_completo', label: 'Mostrar padrón completo' },
  { name: 'mostrar_padron_concentrado', label: 'Mostrar padrón concentrado' },
  { name: 'mostrar_area_calculada', label: 'Mostrar área calculada' },
  { name: 'mostrar_comite_eval_ra', label: 'Mostrar comité eval. RA' },
  { name: 'mostrar_boton_odk_acopio', label: 'Mostrar botón ODK acopio' },
];

const ETIQUETAS_FIELDS = [
  { name: 'anticipos_etiqueta', label: 'Etiqueta de anticipos' },
  { name: 'parcelas_etiqueta', label: 'Etiqueta de parcelas' },
  { name: 'plantas_etiqueta', label: 'Etiqueta de plantas' },
  { name: 'ine_etiqueta', label: 'Etiqueta de INE' },
  { name: 'curp_etiqueta', label: 'Etiqueta de CURP' },
  { name: 'rfc_etiqueta', label: 'Etiqueta de RFC' },
  { name: 'municipio_etiqueta', label: 'Etiqueta de municipio' },
  { name: 'localidad_etiqueta', label: 'Etiqueta de localidad' },
  { name: 'recibo_etiqueta', label: 'Etiqueta de recibo' },
];

// ─── Boolean checkbox subcomponent ───────────────────────────────────────────

const CheckboxField = ({ name, label, register }) => (
  <div className="col-md-4 mb-3">
    <div className="form-check">
      <input id={name} type="checkbox" className="form-check-input" {...register(name)} />
      <label htmlFor={name} className="form-check-label">
        {label} <small className="text-muted">({name})</small>
      </label>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const CooperativasConfigEdit = () => {
  const { id } = useParams();
  const history = useHistory();
  const [blocking, setBlocking] = useState(false);
  const [coopNombre, setCoopNombre] = useState('');

  // Catalog states
  const [currencies, setCurrencies] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [creditos, setCreditos] = useState([]);
  const [ciclos, setCiclos] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {},
  });

  useEffect(() => {
    setBlocking(true);

    const configReq = Api.get('admin/cooperativas', `${id}/config`);
    const ciclosReq = Api.getAll('admin/catalogos/ciclos');
    const currencyReq = Api.getAll('admin/catalogos/cat_currency');
    const unidadesReq = Api.getAll('admin/catalogos/cat_unidades_acopio');
    const conversionsReq = Api.getAll('admin/catalogos/cat_units_conversions');
    const creditosReq = Api.getAll('admin/catalogos/cat_credito_precosecha');

    Promise.all([configReq, ciclosReq, currencyReq, unidadesReq, conversionsReq, creditosReq])
      .then(([configRes, ciclosRes, currencyRes, unidadesRes, conversionsRes, creditosRes]) => {
        const config = configRes.data;

        // Set cooperativa name from relation
        if (config.cooperativa && config.cooperativa.nombre) {
          setCoopNombre(config.cooperativa.nombre);
        }

        // Build catalog option arrays
        setCurrencies(
          (currencyRes.data || []).map(c => ({
            value: c.id,
            label: `${c.name} (${c.symbol} - ${c.iso})`,
          }))
        );
        setUnidades(
          (unidadesRes.data || []).map(u => ({
            value: u.id,
            label: `${u.nombre} (${u.abreviacion})`,
          }))
        );
        setConversions(
          (conversionsRes.data || []).map(cv => ({
            value: cv.id,
            label: `${cv.name} - ${cv.descripcion}`,
          }))
        );
        setCreditos(
          (creditosRes.data || []).map(cr => ({
            value: cr.id,
            label: cr.nombre + ' - ' + cr.interes / 100 + '%',
          }))
        );
        setCiclos(
          (ciclosRes.data || []).map(c => ({
            value: c.id,
            label: c.nombre,
          }))
        );

        // Populate form — boolean fields: convert 0/1 to true/false for checkboxes
        const booleanNames = [...MODULES_FIELDS, ...ACOPIO_FIELDS, ...VISUALIZACION_FIELDS].map(f => f.name);

        const formValues = { ...config };
        booleanNames.forEach(name => {
          if (formValues[name] !== undefined) {
            formValues[name] = formValues[name] == 1;
          }
        });

        // Remove the cooperativa relation object so it doesn't pollute form data
        delete formValues.cooperativa;

        reset(formValues);
        setBlocking(false);
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.log('Error loading config:', e);
        setBlocking(false);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la configuración.',
        });
      });
  }, [id]);

  const onSubmit = values => {
    // Convert boolean checkboxes back to 0/1 integers
    const booleanNames = [...MODULES_FIELDS, ...ACOPIO_FIELDS, ...VISUALIZACION_FIELDS].map(f => f.name);

    const payload = { ...values };
    booleanNames.forEach(name => {
      if (payload[name] !== undefined) {
        payload[name] = payload[name] ? 1 : 0;
      }
    });

    // id_cooperativa must never be sent
    delete payload.id_cooperativa;

    setBlocking(true);
    Api.update(`admin/cooperativas/${id}`, 'config', payload)
      .then(() => {
        setBlocking(false);
        Swal.fire({
          icon: 'success',
          title: '',
          text: 'Configuración guardada con éxito.',
        }).then(() => {
          history.push('/admin/cooperativas/list');
        });
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.log('Error saving config:', e);
        setBlocking(false);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo guardar la configuración.',
        });
      });
  };

  return (
    <>
      <div className="row justify-content-between">
        <h1 className="page-header">Configuración Cooperativa{coopNombre ? `: ${coopNombre}` : ''}</h1>
        <Link to={{ pathname: '/admin/cooperativas/list' }} style={{ marginTop: '-26px' }}>
          <button type="button" className="btn btn-outline-secondary mt-4" style={{ borderRadius: '20px 20px 20px 20px' }}>
            <i className="fa fa-arrow-left"></i> Volver a Cooperativas
          </button>
        </Link>
      </div>

      <div className="row">
        <div className="col-md-12">
          <Panel>
            <PanelBody>
              <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {/* ── Módulos activos ─────────────────────────────────── */}
                  <h5 className="mt-2 mb-3 border-bottom pb-2">Módulos activos</h5>
                  <div className="row">
                    {MODULES_FIELDS.map(f => (
                      <CheckboxField key={f.name} name={f.name} label={f.label} register={register} />
                    ))}
                  </div>

                  {/* ── Opciones de acopio ──────────────────────────────── */}
                  <h5 className="mt-4 mb-3 border-bottom pb-2">Opciones de acopio</h5>
                  <div className="row">
                    {ACOPIO_FIELDS.map(f => (
                      <CheckboxField key={f.name} name={f.name} label={f.label} register={register} />
                    ))}
                  </div>

                  {/* ── Opciones de visualización ───────────────────────── */}
                  <h5 className="mt-4 mb-3 border-bottom pb-2">Opciones de visualización</h5>
                  <div className="row">
                    {VISUALIZACION_FIELDS.map(f => (
                      <CheckboxField key={f.name} name={f.name} label={f.label} register={register} />
                    ))}
                  </div>

                  {/* ── Etiquetas personalizadas ────────────────────────── */}
                  <h5 className="mt-4 mb-3 border-bottom pb-2">Etiquetas personalizadas</h5>
                  <div className="row">
                    {ETIQUETAS_FIELDS.map(f => (
                      <div key={f.name} className="col-md-6 mb-3">
                        <label htmlFor={f.name}>
                          {f.label} <small className="text-muted">({f.name})</small>
                        </label>
                        <input id={f.name} type="text" className="form-control" {...register(f.name)} />
                      </div>
                    ))}
                  </div>

                  {/* ── Ciclos activos ──────────────────────────────────── */}
                  <h5 className="mt-4 mb-3 border-bottom pb-2">Ciclos activos</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>
                        Ciclo SIC activo <small className="text-muted">(id_ciclo_sic)</small>
                      </label>
                      <Controller
                        name="id_ciclo_sic"
                        control={control}
                        render={({ field }) => (
                          <Select
                            inputId="id_ciclo_sic"
                            options={ciclos}
                            value={ciclos.find(o => o.value === field.value) || null}
                            onChange={opt => field.onChange(opt ? opt.value : null)}
                            placeholder="Seleccionar ciclo..."
                            isClearable={false}
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>
                        Ciclo de acopio activo <small className="text-muted">(id_ciclo_acopio)</small>
                      </label>
                      <Controller
                        name="id_ciclo_acopio"
                        control={control}
                        render={({ field }) => (
                          <Select
                            inputId="id_ciclo_acopio"
                            options={ciclos}
                            value={ciclos.find(o => o.value === field.value) || null}
                            onChange={opt => field.onChange(opt ? opt.value : null)}
                            placeholder="Seleccionar ciclo..."
                            isClearable={false}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* ── Unidades y moneda ───────────────────────────────── */}
                  <h5 className="mt-4 mb-3 border-bottom pb-2">Unidades y moneda</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>
                        Moneda <small className="text-muted">(id_cat_currency)</small>
                      </label>
                      <Controller
                        name="id_cat_currency"
                        control={control}
                        render={({ field }) => (
                          <Select
                            inputId="id_cat_currency"
                            options={currencies}
                            value={currencies.find(o => o.value === field.value) || null}
                            onChange={opt => field.onChange(opt ? opt.value : null)}
                            placeholder="Seleccionar moneda..."
                            isClearable={true}
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>
                        Unidad de acopio principal <small className="text-muted">(id_cat_unidades_acopio)</small>
                      </label>
                      <Controller
                        name="id_cat_unidades_acopio"
                        control={control}
                        render={({ field }) => (
                          <Select
                            inputId="id_cat_unidades_acopio"
                            options={unidades}
                            value={unidades.find(o => o.value === field.value) || null}
                            onChange={opt => field.onChange(opt ? opt.value : null)}
                            placeholder="Seleccionar unidad..."
                            isClearable={false}
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>
                        Unidad de acopio extra <small className="text-muted">(id_cat_unidades_acopio_extra)</small>
                      </label>
                      <Controller
                        name="id_cat_unidades_acopio_extra"
                        control={control}
                        render={({ field }) => (
                          <Select
                            inputId="id_cat_unidades_acopio_extra"
                            options={unidades}
                            value={unidades.find(o => o.value === field.value) || null}
                            onChange={opt => field.onChange(opt ? opt.value : null)}
                            placeholder="Seleccionar unidad extra..."
                            isClearable={true}
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>
                        Conversión de unidades en acopio <small className="text-muted">(id_cat_units_conversions_acopio)</small>
                      </label>
                      <Controller
                        name="id_cat_units_conversions_acopio"
                        control={control}
                        render={({ field }) => (
                          <Select
                            inputId="id_cat_units_conversions_acopio"
                            options={conversions}
                            value={conversions.find(o => o.value === field.value) || null}
                            onChange={opt => field.onChange(opt ? opt.value : null)}
                            placeholder="Seleccionar conversión..."
                            isClearable={true}
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>
                        Tipo de crédito precosecha <small className="text-muted">(id_cat_credito_precosecha)</small>
                      </label>
                      <Controller
                        name="id_cat_credito_precosecha"
                        control={control}
                        render={({ field }) => (
                          <Select
                            inputId="id_cat_credito_precosecha"
                            options={creditos}
                            value={creditos.find(o => o.value === field.value) || null}
                            onChange={opt => field.onChange(opt ? opt.value : null)}
                            placeholder="Seleccionar tipo de crédito..."
                            isClearable={false}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* ── Otros ───────────────────────────────────────────── */}
                  <h5 className="mt-4 mb-3 border-bottom pb-2">Otros</h5>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label htmlFor="rendimiento_exportable">
                        Rendimiento exportable <small className="text-muted">(rendimiento_exportable)</small>
                      </label>
                      <input
                        id="rendimiento_exportable"
                        type="number"
                        step="any"
                        className="form-control"
                        {...register('rendimiento_exportable', { valueAsNumber: true })}
                      />
                    </div>
                  </div>

                  {/* ── Submit ──────────────────────────────────────────── */}
                  <div className="row mt-3">
                    <div className="col-md-3 offset-md-9">
                      <button className="btn btn-sirio btn-block" type="submit">
                        Guardar Configuración
                      </button>
                    </div>
                  </div>
                </form>
              </BlockUi>
            </PanelBody>
          </Panel>
        </div>
      </div>
    </>
  );
};

export default CooperativasConfigEdit;
